import * as React from "react"
import { format, parse, isValid } from "date-fns"
import { cs } from "date-fns/locale"
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export function DatePicker({ value, onChange, placeholder = "Vyberte datum", className, ...props }) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState(value || '')
  const [calendarMonth, setCalendarMonth] = React.useState(new Date())
  
  // Sync inputValue with external value
  React.useEffect(() => {
    setInputValue(value || '')
  }, [value])
  
  // Parse the value if it's a string in DD.MM.YYYY format
  const dateValue = React.useMemo(() => {
    if (!value) return undefined
    if (value instanceof Date) return value
    // Try to parse DD.MM.YYYY format
    try {
      const parsed = parse(value, 'dd.MM.yyyy', new Date())
      if (isValid(parsed)) return parsed
    } catch (e) {}
    // Try ISO format
    try {
      const parsed = new Date(value)
      if (isValid(parsed)) return parsed
    } catch (e) {}
    return undefined
  }, [value])

  // Update calendar month when value changes
  React.useEffect(() => {
    if (dateValue) {
      setCalendarMonth(dateValue)
    }
  }, [dateValue])

  const handleSelect = (date) => {
    if (date) {
      const formatted = format(date, 'dd.MM.yyyy')
      onChange(formatted)
      setInputValue(formatted)
    } else {
      onChange('')
      setInputValue('')
    }
    setOpen(false)
  }

  const handleInputChange = (e) => {
    const val = e.target.value
    setInputValue(val)
    
    // Try to parse when user types a complete date
    if (val.length === 10) {
      try {
        const parsed = parse(val, 'dd.MM.yyyy', new Date())
        if (isValid(parsed)) {
          onChange(val)
          setCalendarMonth(parsed)
        }
      } catch (e) {}
    }
  }

  const handleInputBlur = () => {
    // Validate on blur
    if (inputValue) {
      try {
        const parsed = parse(inputValue, 'dd.MM.yyyy', new Date())
        if (isValid(parsed)) {
          const formatted = format(parsed, 'dd.MM.yyyy')
          onChange(formatted)
          setInputValue(formatted)
        } else {
          // Reset to previous valid value
          setInputValue(value || '')
        }
      } catch (e) {
        setInputValue(value || '')
      }
    } else {
      onChange('')
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur()
      setOpen(false)
    }
  }

  // Generate year options (current year -10 to +20)
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 31 }, (_, i) => currentYear - 10 + i)
  
  const months = [
    'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
    'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'
  ]

  const handleYearChange = (year) => {
    const newDate = new Date(calendarMonth)
    newDate.setFullYear(parseInt(year))
    setCalendarMonth(newDate)
  }

  const handleMonthChange = (month) => {
    const newDate = new Date(calendarMonth)
    newDate.setMonth(parseInt(month))
    setCalendarMonth(newDate)
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
          {...props}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? value : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[100]" align="start">
        <div className="p-3 space-y-3">
          {/* Manual date input */}
          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="DD.MM.YYYY"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              className="text-center font-mono"
              maxLength={10}
            />
          </div>
          
          {/* Month and Year selectors */}
          <div className="flex items-center gap-2">
            <Select 
              value={calendarMonth.getMonth().toString()} 
              onValueChange={handleMonthChange}
            >
              <SelectTrigger className="flex-1 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[110]">
                {months.map((month, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={calendarMonth.getFullYear().toString()} 
              onValueChange={handleYearChange}
            >
              <SelectTrigger className="w-24 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[110] max-h-60">
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          month={calendarMonth}
          onMonthChange={setCalendarMonth}
          locale={cs}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}
